import { Context } from '@front10/helpers/dist/Context';
import type Leaflet from 'leaflet';
import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type ReactLeaflet from 'react-leaflet';
import bezierSpline from '@turf/bezier-spline';
import type { Map as LeafletMap } from 'react-leaflet';
import type ReactLeafletControl from 'react-leaflet-control';
import useSupercluster from 'use-supercluster';
import useStyles from '@front10/helpers/dist/UseStyles';
import FareItemCard from '../../items/FareItemCard';
import CheckAvailabilityCard from './markers/CheckAvailabilityCard';
import dayjs from 'dayjs';
import { renderMarkersFn } from './markers/renderMarker';
import { renderClusterFn } from './markers/renderCluster';
import { renderBeginRouteMarkerFn } from './markers/renderBeginRouteMarker';
import { renderRouteLineFn } from './markers/renderRouteLine';
import { renderEndRouteMarkerFn } from './markers/renderEndRouteMarker';
import { isValidLocation } from './helpers/helper';
//-------------------------------------
// Hooks
//-------------------------------------
import { Coordinates } from './filters/useLocations';
import {
  startUserInteractionSelector,
  isFullScreenSelector,
  toggleFullScreenSelector,
  isShowTopOffersSelector,
  setZoomSelector,
  isAlertShowSelector,
  hideAlertInfoSelector,
  setVisibleDestinationsSelector,
  getPreDestinationsSelector,
  visibleDestinationsSelector,
  useStore,
  getChangedOrigin,
  setShowNoResult,
  getSelectedOrigin,
  setChangedOrigin
} from './hooks/useIMCState';
import useVisibleMarkers from './hooks/useVisibleMarkers';

//-------------------------------------
// Atoms
//-------------------------------------
import Box from '../../elements/Box/Box';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import Text from '../../elements/Text';
import AlertInfo from '../../others/Alert';
import { MarkerData } from './classes/MarkerData';
import BeginRouteMarker from './markers/BeginRouteMarker';
import EndRouteMarker from './markers/EndRouteMarker';
import NameMarker from './markers/NameMarker';
import CardControlCheckbox from './atoms/CardControl';
//-------------------------------------
// Commons
//-------------------------------------
import { nextTick } from '../../../helpers/date';
import { debounce } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { notifyExitFullMode, notifyFullMode } from '../_commons/navigation';
import VectorMapMarker, { VectorMapMarkerPropsType } from './markers/VectorMapMarker';
import { FareMarkerData } from './classes/MarkerData';

import { isNoFareMarker } from '../InteractiveMapContainer/classes/MarkerData';
import { strFormatV2 } from '../../../helpers/utils';

const flightTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

const STYLE_BY_MODE: any = {
  standard: {
    style: {
      color: '#CCCFD5',
      fillColor: '#EDEDEE',
      waterColor: '#9CD5FF',
      fillOpacity: 1,
      opacity: 0.8,
      weight: 2
    }
  },
  dark: {
    style: {
      color: '#E5E5E5',
      fillColor: '#2C2C2D',
      fillOpacity: 1,
      opacity: 0.8,
      weight: 2,
      waterColor: '#070707'
    }
  },
  light: {
    style: {
      color: '#BABABA',
      fillColor: '#FAFAFA',
      waterColor: '#E6E6E6',
      fillOpacity: 1,
      opacity: 0.8,
      weight: 2
    }
  }
};

type ReadingTypes = 'ONE_WAY' | 'ROUND_TRIP' | 'MULTI_CITY' | 'EACH_WAY';

export interface VectorMapProps {
  beginRouteSettings?: $TSFixMe;
  endRouteSettings?: $TSFixMe;
  ref: $TSFixMe;
  showFullScreenControl?: boolean;
  showCenterControl?: boolean;
  initCenter: Coordinates;
  geoJSONSettings?: $TSFixMe;
  geoJSONData: $TSFixMe;
  polygonSettings?: $TSFixMe;
  polylineSettings?: $TSFixMe;
  polylineSettingsDestinationHover?: $TSFixMe;
  markersSettings?: Partial<VectorMapMarkerPropsType>;
  mapSettings?: $TSFixMe;
  markers?: MarkerData[];
  destinationMapReadyOnly?: MarkerData[];
  markerNames: MarkerData[];
  fareMarkers?: MarkerData[];
  route?: MarkerData[];
  fullScreenBtnSettings?: $TSFixMe;
  fullScreenBtnIconSettings?: $TSFixMe;
  fullScreenBtnContainerSettings?: $TSFixMe;
  centerBtnSettings?: $TSFixMe;
  centerBtnIconSettings?: $TSFixMe;
  centerBtnContainerSettings?: $TSFixMe;
  zoomControlsSettings?: $TSFixMe;
  zoomControlsContainerSettings?: $TSFixMe;
  zoomInBtnSettings?: $TSFixMe;
  zoomInBtnIconSettings?: $TSFixMe;
  showZoomControl?: boolean;
  showZoomValue?: boolean;
  zoomOutBtnSettings?: $TSFixMe;
  zoomOutBtnIconSettings?: $TSFixMe;
  isMobile?: boolean;
  onZoomEnd?: (newZoom: number, oldZoom: number) => void;
  onDragEnd?: () => void;
  handleCloseCardMap?: () => void;
  maxBounds?: $TSFixMe;
  isOriginSelected?: boolean;
  setShowEndMarker: React.Dispatch<React.SetStateAction<boolean>>;
  setNewRouteMarker: React.Dispatch<React.SetStateAction<MarkerData | undefined | null>>;
  oldEndRouteMarker: MarkerData | undefined | null;
  showEndMarker: boolean;
  enableCluster?: boolean;
  originToDestinationLine?: boolean;
  clusterSettings?: $TSFixMe;
  clusterSizes?: $TSFixMe;
  markerClusterSettings?: $TSFixMe;
  displayRouteOnHover?: boolean;
  overlayLabelKey?: string;
  overlayLabelProps?: $TSFixMe;
  overlayProps?: $TSFixMe;
  centerCardOverlayProps?: $TSFixMe;
  showCloseButton?: boolean;
  panOptions?: Leaflet.PanOptions;
  markerHover: FareMarkerData | MarkerData | undefined;
  setMarkerHover: React.Dispatch<React.SetStateAction<FareMarkerData | MarkerData | undefined>>;
  enableHoverShapeCluster?: boolean;
  isLoading?: boolean;
  showCardControl?: boolean;
  cardControlBtnContainerSettings?: $TSFixMe;
  cardControlBtnSettings?: $TSFixMe;
  infoControlBtnContainerSettings?: $TSFixMe;
  infoControlBtnSettings?: $TSFixMe;
  setCurrentCenter?: $TSFixMe;
  currentBound?: $TSFixMe;
  enableCenterCard?: boolean;
  setIsClearDestination?: $TSFixMe;
  filterState?: $TSFixMe;
  topMargin?: number;
  bottomMargin?: number;
  showInitialData?: boolean;
  mapMode?: {
    mode: string;
    style?: any;
  };
  centerOriginLogic?: boolean;
  enabledCyclicMapBehavior?: boolean;
  cyclicMapPaddingWidth?: number;
  initialViewDestinationRegion?: string;
}

export interface MapActions {
  updateSize: () => void;
  setZoom: (zoom?: number) => void;
  setView: (position?: Coordinates, zoom?: number) => void;
  moveTo: (position: Coordinates) => void;
  centerMap: () => void;
  cleanTooltips: () => void;
  clearVisibleMarkers: () => void;
  getCurrentCenter: () => any;
  isMarkerContainedInBounds: (lat: number, long: number) => boolean;
  resetView: (location: any) => void;
  updateVisibleMarkers: () => void;
  handleZoomIn?: () => void;
  handleZoomOut?: () => void;
  actualZoom?: () => number;
}

interface Position {
  topleft: 'topright';
  topright: 'topleft';
  bottomleft: 'bottomright';
  bottomright: 'bottomleft';
}

const VectorMap: FunctionComponent<VectorMapProps> = React.forwardRef<MapActions, VectorMapProps>(
  (props: VectorMapProps, ref) => {
    if (isSSR()) {
      return <Box>SSR is not supported</Box>;
    }
    return <Map {...props} ref={ref} />;
  }
);

const Map: FunctionComponent<VectorMapProps> = React.forwardRef<MapActions, VectorMapProps>(
  (props: VectorMapProps, ref) => {
    const {
      geoJSONData,
      initCenter,
      fullScreenBtnSettings = {},
      fullScreenBtnIconSettings = {},
      fullScreenBtnContainerSettings = {},
      centerBtnContainerSettings = {},
      centerBtnSettings = {},
      centerBtnIconSettings = {},
      markers = [],
      destinationMapReadyOnly = [],
      fareMarkers = [],
      route = [],
      polylineSettings = {},
      polylineSettingsDestinationHover = {},
      markersSettings = {},
      mapSettings = {},
      geoJSONSettings = {
        canvasPadding: 0.1,
        style: {
          color: '#CCCFD5',
          fillColor: '#EDEDEE',
          waterColor: '#9CD5FF',
          fillOpacity: 1,
          opacity: 0.8,
          weight: 2
        }
      },
      isMobile = false,
      showFullScreenControl = true,
      showCenterControl = true,
      zoomInBtnSettings = {},
      zoomInBtnIconSettings = {},
      zoomControlsSettings = {},
      zoomControlsContainerSettings = {},
      showZoomControl = true,
      showZoomValue,
      zoomOutBtnSettings = {},
      zoomOutBtnIconSettings = {},
      beginRouteSettings = {},
      endRouteSettings = {},
      enabledCyclicMapBehavior = false,
      cyclicMapPaddingWidth = 30,
      maxBounds = enabledCyclicMapBehavior
        ? { northEast: [90, -546], southWest: [-90, 546] }
        : {
            northEast: [90, -180],
            southWest: [-90, 180]
          },
      isOriginSelected,
      setShowEndMarker,
      showEndMarker,
      oldEndRouteMarker,
      enableCluster = true,
      originToDestinationLine = true,
      topMargin = 13,
      bottomMargin = 5,
      clusterSettings = {},
      panOptions = { animate: true, duration: 1 },
      clusterSizes = {
        Small: {
          min: 2,
          max: 9
        },
        Medium: {
          min: 10,
          max: 29
        },
        Large: {
          min: 30,
          max: 49
        },
        LargeXL: {
          min: 50,
          max: 999
        }
      },
      markerClusterSettings = {
        'cmp-styles': {
          'background-color': 'rgba(8,2,6,0.7)'
        }
      },
      displayRouteOnHover = true,
      overlayLabelKey = 'cmp-map-mobile-activate-interactive-message',
      overlayLabelProps,
      overlayProps,
      centerCardOverlayProps,
      markerHover,
      setMarkerHover,
      enableHoverShapeCluster = true,
      isLoading,
      showCardControl = true,
      cardControlBtnContainerSettings = {},
      cardControlBtnSettings = {},
      infoControlBtnContainerSettings = {},
      infoControlBtnSettings = {},
      markerNames,
      setCurrentCenter,
      enableCenterCard = true,
      filterState,
      showInitialData,
      centerOriginLogic = true
    } = props;
    const selectedOrigin: any = useStore(getSelectedOrigin);
    const getChangedOriginFn = useStore(getChangedOrigin);
    const setChangedOriginFn = useStore(setChangedOrigin);
    const [zIndex, setZIndex] = useState(999);

    const newOverlayLabelProps = {
      marginWidth: 'auto',
      marginHeight: 'auto',
      textAlign: 'center',
      fontSize: '5',
      textColor: 'white',
      style: {
        textShadow: '2px 2px 2px rgba(0,0,0,.5)'
      },
      ...overlayLabelProps
    };

    const newOverlayProps = {
      position: 'absolute',
      width: '100',
      height: '100',
      display: 'flex',
      style: {
        zIndex: '5',
        cursor: 'pointer',
        background: 'rgba(0, 0, 0, 0.25)'
      },
      ...overlayProps
    };

    const newCenterCardOverlayProps = {
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      width: '100',
      height: '100',
      display: 'flex',
      style: {
        zIndex: '1000',
        cursor: 'pointer',
        background: 'rgba(0, 0, 0, 0.25)'
      },
      ...centerCardOverlayProps
    };

    const { translate, direction, theme } = useContext(Context);
    const [classNameCluster] = useStyles(markerClusterSettings, { direction, theme });
    const mapColor =
      props?.mapMode?.style?.map?.waterColor ??
      (props?.mapMode?.mode
        ? STYLE_BY_MODE[props?.mapMode?.mode?.toLowerCase()]?.style?.waterColor
        : '#AADAFF');
    const [classNameColor] = useStyles(
      {
        'cmp-styles': {
          'background-color': `${mapColor} !important`
        }
      },
      {
        direction,
        theme
      }
    );

    const isFullScreen = useStore(isFullScreenSelector);
    const toggleFullScreen = useStore(toggleFullScreenSelector);
    const setZoomGlobalState = useStore(setZoomSelector);
    const isShowTopOffers = useStore(isShowTopOffersSelector);
    const preDestination = useStore(getPreDestinationsSelector);
    const mapRef = useRef<LeafletMap>(null);
    const maxZoom = mapSettings.maxZoom || 12;
    const minZoom = mapSettings.zoomIn || 4;
    const startZoom =
      mapSettings.startZoom < minZoom
        ? minZoom
        : mapSettings.startZoom > maxZoom
        ? maxZoom
        : mapSettings.startZoom || 4;
    const deltaZoom = mapSettings.zoomDelta || 0.5;
    const [actualZoom, setActualZoom] = useState(startZoom);
    const [overlay, setOverlay] = useState(isMobile);
    const [polygon, setPolygon] = useState([]);
    const [viewPortChange, setViewPortChange] = useState(0);

    const { Map, Marker, Polygon, GeoJSON } = require('react-leaflet') as typeof ReactLeaflet;
    const Control = require('react-leaflet-control').default as typeof ReactLeafletControl;
    const leaflet = require('leaflet') as typeof Leaflet;
    const mapBounds = mapRef.current && mapRef.current.leafletElement.getBounds();

    // const oldOrigin = React.useRef(filterState.origin.value);

    const newMarkers: any = useMemo(() => {
      const isLocationInBounds = (lat: number, long: number) => {
        if (mapBounds && mapBounds.contains(leaflet.latLng(lat, long))) {
          return true;
        }
        return false;
      };
      if (enabledCyclicMapBehavior) {
        const markers_ = [];
        for (let index = 0; index < markers.length; index++) {
          const marker = markers[index];
          if (marker?.location && isValidLocation(marker?.location)) {
            markers_.push(marker);
            if (isLocationInBounds(marker?.location[0], marker?.location[1] - 360)) {
              markers_.push({
                ...marker,
                location: [marker?.location[0], marker?.location[1] - 360]
              });
            }
            if (isLocationInBounds(marker?.location[0], marker?.location[1] + 360)) {
              markers_.push({
                ...marker,
                location: [marker?.location[0], marker?.location[1] + 360]
              });
            }
          }
        }
        return markers_;
      } else {
        return markers;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markers, enabledCyclicMapBehavior]);
    const { clearMarkers: clearVisibleMarkers, getNewVisibleMarkers } = useVisibleMarkers(
      newMarkers,
      mapRef
    );

    useEffect(() => {
      mapRef?.current?.container
        ?.querySelector('.leaflet-zoom-animated')
        ?.setAttribute('aria-hidden', 'true');
      if (getChangedOriginFn && selectedOrigin) {
        const location = selectedOrigin.geo
          ? [selectedOrigin.geo.latitude, selectedOrigin.geo.longitude]
          : selectedOrigin.location;
        new Promise((resolve) => {
          if (mapRef.current) {
            const map = mapRef.current.leafletElement;
            if (centerOriginLogic) {
              map.panTo(location, panOptions);
            }
            map.on('moveend zoomend', () => {
              setTimeout(() => removeTabIndex(), 500);
              resolve(true);
            });
          }
          resolve(false);
        }).then(() => {
          setTimeout(() => {
            setChangedOriginFn(false);
          }, 1000);
        });
      } else {
        if (isOriginSelected) {
          let preDest = [];
          if (enabledCyclicMapBehavior) {
            const mapMarkers: Record<string, string> = {};
            preDest = preDestination
              ?.filter(({ value, location }) => {
                if (mapBounds?.contains(leaflet.latLng(location[0], location[1]))) {
                  if (mapMarkers[value] === undefined) {
                    mapMarkers[value] = value;
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return false;
                }
              })
              .map((item) => item.value);
          } else {
            preDest = preDestination
              ?.filter(({ location }) =>
                mapBounds?.contains(leaflet.latLng(location[0], location[1]))
              )
              .map((item) => item.value);
          }
          if (preDest && preDest.length !== 0) {
            setVisibleDestinations(preDest);
          } else if (preDest && preDest.length === 0) {
            setVisibleDestinations(null);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapRef?.current, preDestination, getChangedOriginFn]);

    const isMarkerContainedInBounds = useCallback(
      (lat: number, long: number) => {
        if (mapBounds && mapBounds.contains(leaflet.latLng(lat, long))) {
          return true;
        }
        return false;
      },
      [leaflet, mapBounds]
    );

    useEffect(() => {
      setTimeout(() => removeTabIndex(), 1000);
    }, [markers, isFullScreen]);

    const jsonRef = useRef<ReactLeaflet.GeoJSON>(null);

    const GeoJsonMap = React.useCallback(
      (): JSX.Element => {
        const style = props?.mapMode?.mode
          ? props?.mapMode?.style?.map
            ? { style: props?.mapMode?.style?.map }
            : STYLE_BY_MODE[props?.mapMode?.mode.toLowerCase()]
          : { ...geoJSONSettings };

        return (
          geoJSONData && (
            <GeoJSON
              data={geoJSONData}
              ref={jsonRef}
              pane="tilePane"
              interactive={false}
              fillColor="#ededee"
              color="#cccfd5"
              opacity={0.8}
              fillOpacity={1}
              weight={2}
              style={{
                style: 'solid'
              }}
              {...style}
            />
          )
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [geoJSONData, geoJSONSettings, leaflet]
    );

    useEffect(() => {
      setZoomGlobalState(actualZoom);
    }, [actualZoom, setZoomGlobalState]);

    const moveTo = useCallback(
      (position: Coordinates) => {
        return new Promise((resolve) => {
          if (mapRef.current) {
            const map = mapRef.current.leafletElement;
            map.panTo(position, panOptions);
            map.on('moveend zoomend', () => {
              setTimeout(() => removeTabIndex(), 500);
              resolve(true);
            });
          }
          resolve(false);
        });
      },
      [panOptions]
    );

    const updateSize = useCallback(() => {
      return new Promise((resolve) => {
        nextTick(() => {
          if (mapRef.current) {
            const map = mapRef.current.leafletElement;
            map.invalidateSize();
            map.on('resize', () => resolve(true));
          } else {
            resolve(false);
          }
        });
      });
    }, []);

    const setZoom = useCallback(
      (zoom: number = startZoom) => {
        return new Promise((resolve) => {
          if (mapRef.current) {
            setActualZoom(zoom);
            const map = mapRef.current.leafletElement;
            map.invalidateSize();
            resolve(true);
          } else {
            resolve(false);
          }
        });
      },
      [startZoom]
    );

    const setView = (center: Coordinates = initCenter, zoom: number = startZoom) => {
      setTimeout(() => {
        if (mapRef.current) {
          setActualZoom(zoom);
          mapRef.current.leafletElement.setView(center, zoom, { animate: true });
        }
      });
    };

    function updateVisibleMarkers() {
      if (enabledCyclicMapBehavior) {
        setViewPortChange((viewPortChange) => viewPortChange + 1);
      }
      handleViewportChanged();
      startUserInteraction();
      setCurrentCenter(mapRef?.current?.leafletElement.getCenter());
    }

    useEffect(() => {
      setTimeout(() => updateVisibleMarkers(), 500);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFullScreen]);

    useEffect(() => {
      if (isOriginSelected && centerOriginLogic) {
        handleCenterMap();
      }
      if (!isOriginSelected && visibleDestinations && visibleDestinations?.length > 0) {
        setVisibleDestinations([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOriginSelected]);

    const handleCenterMap = () => {
      if (route.length >= 1) {
        moveTo(route[0].location).then(() => setTimeout(() => updateVisibleMarkers(), 1000));
      }
    };

    const centerMap = useCallback(() => {
      mapRef.current?.leafletElement?.invalidateSize();
      setZoom(startZoom).then(() => setTimeout(() => moveTo(initCenter), 500));
    }, [initCenter, moveTo, setZoom, startZoom]);

    const cleanTooltips = useCallback(() => {
      mapRef.current?.leafletElement?.eachLayer((layer: $TSFixMe) => {
        if (layer.options.pane === 'tooltipPane') {
          layer.removeFrom(mapRef.current?.leafletElement);
        }
      });
    }, []);

    const handleToggleFullScreen = async () => {
      if (!isFullScreen) {
        notifyFullMode();
      } else {
        notifyExitFullMode();
      }
      toggleFullScreen(!isFullScreen);
      setOverlay(!isFullScreen);
    };

    const handleViewportChanged = async () => {
      getNewVisibleMarkers()
        .then((markers) => {
          if (enabledCyclicMapBehavior === true) {
            const mapMarkers: Record<string, string> = {};
            const newMarkers: string[] = [];
            markers.forEach((marker) => {
              if (mapMarkers[marker.value] === undefined) {
                mapMarkers[marker.value] = marker.value;
                newMarkers.push(marker.value);
              }
            });
            setVisibleDestinations(newMarkers);
          } else {
            setVisibleDestinations(markers.map((marker) => marker.value));
          }
        })
        .catch(() => {});
    };

    const setShowNoResultFn = useStore(setShowNoResult);
    const startUserInteraction = useStore(startUserInteractionSelector);
    const setVisibleDestinations = useStore(setVisibleDestinationsSelector);
    const visibleDestinations = useStore(visibleDestinationsSelector);
    const showAlertInfo = useStore(isAlertShowSelector);
    const hideAlertInfo = useStore(hideAlertInfoSelector);

    useEffect(() => {
      if (visibleDestinations && visibleDestinations.length === 0) {
        setShowNoResultFn(true);
      } else {
        setShowNoResultFn(false);
      }
    }, [setShowNoResultFn, visibleDestinations]);

    const handleDragEnd = () => {
      if (props.isOriginSelected) {
        props.onDragEnd?.();
      }
      updateVisibleMarkers();
    };

    const handleDragStart = () => {
      makeMoveRef.current = false;
    };

    // TODO: Update map view on drag for reset canvas
    useEffect(() => {
      const map = mapRef.current;
      const dragUpdate = () => {
        if (enabledCyclicMapBehavior) {
          const debounceCall = debounce(() => map?.leafletElement.fireEvent('viewreset'), 100);
          debounceCall();
        }
      };

      map?.leafletElement.on('drag', dragUpdate);

      return () => {
        map?.leafletElement.off('drag', dragUpdate);
      };
    }, [enabledCyclicMapBehavior]);

    const handleZoomEnd = (event: $TSFixMe) => {
      const newZoom = event.target._zoom;
      props.onZoomEnd?.(actualZoom, newZoom);
      setActualZoom(newZoom);
      updateVisibleMarkers();
      if (enableHoverShapeCluster) {
        setPolygon([]);
      }
      setTimeout(() => removeTabIndex(), 500);
    };

    const handleZoomIn = () => {
      if (actualZoom <= maxZoom) {
        mapRef.current?.leafletElement.zoomIn(deltaZoom, { animate: true });
      }
    };

    const handleZoomOut = () => {
      if (actualZoom >= minZoom) {
        mapRef.current?.leafletElement.zoomOut(deltaZoom, { animate: true });
      }
    };

    const handleClick = () => {
      if (props.showCloseButton === false) {
        setShowEndMarker(false);
        props.handleCloseCardMap?.();
      }
    };

    const onClickCloseBtn = () => {
      setShowEndMarker(false);
      props.handleCloseCardMap?.();
      props.setNewRouteMarker(undefined);
      setMarkerHover(undefined);
      props.setIsClearDestination(true);
    };

    useImperativeHandle(ref, () => ({
      updateSize,
      setZoom,
      setView,
      moveTo,
      centerMap,
      clearVisibleMarkers,
      cleanTooltips,
      isMarkerContainedInBounds,
      getCurrentCenter: () => {
        return mapRef?.current?.leafletElement.getCenter();
      },
      resetView: (location) => {
        mapRef.current?.leafletElement.setView(location, startZoom, { animate: true });
        setCurrentCenter(location);
      },
      updateVisibleMarkers: () => {
        updateVisibleMarkers();
      }
    }));

    const clusterSizesKeys = useMemo(() => Object.keys(clusterSizes), [clusterSizes]);

    const createClusterCustomIcon = (counter: any) => {
      let count = counter;
      if (typeof counter === 'object') {
        count = counter.getChildCount();
      }
      let size = '';
      clusterSizesKeys.forEach((key) => {
        if (count >= clusterSizes[key].min && count <= clusterSizes[key].max) {
          size = key;
        }
      });

      const options = {
        cluster: `markerCluster${size}`,
        circle4: `markerCluster${size}DivFour`,
        label: `markerCluster${size}Label`
      };

      return leaflet.divIcon({
        html: `<div class="${options.circle4} ${classNameCluster}">
            <span class="${options.label}">${count}</span>
          </div>`,
        className: `marker-cluster-point ${options.cluster}`
      });
    };

    const handleSetMarkerHover = (marker: FareMarkerData | MarkerData | undefined) =>
      setMarkerHover(marker);

    const removeTabIndex = () => {
      if (mapRef.current) {
        const elements = document.querySelectorAll('.marker-cluster-point');
        elements.forEach((e) => e.setAttribute('tabindex', '-1'));
      }
    };
    const renderMarkers = useMemo(() => {
      if (!enableCluster && showInitialData && !isOriginSelected) {
        return markers
          .filter((e) => JSON.stringify(e) !== JSON.stringify(oldEndRouteMarker))
          .map((marker, index) => {
            return (
              <VectorMapMarker
                onMouseOver={handleSetMarkerHover}
                key={`marker-${index}`}
                showTopOffers={isShowTopOffers}
                marker={marker}
                isMapMode={!!props?.mapMode?.mode}
                markerHover={markerHover}
                mapRef={mapRef}
                zIndex={zIndex}
                setZIndex={setZIndex}
                {...markersSettings}
              />
            );
          });
      }
      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableCluster, markers, oldEndRouteMarker, markerHover]);

    const renderFareMarkers = useMemo(() => {
      if (!enabledCyclicMapBehavior) {
        // TODO: It was necessary to filter since the popup opens automatically
        //       and is positioned according to where it has available space that
        //       formerly when positioned above the fareMarker was covered and was not seen.
        return fareMarkers.map((marker, index) => {
          return (
            <VectorMapMarker
              onMouseOver={handleSetMarkerHover}
              key={`marker-${index}`}
              showTopOffers={
                isShowTopOffers && JSON.stringify(marker) !== JSON.stringify(oldEndRouteMarker)
              }
              marker={marker}
              isMapMode={!!props?.mapMode?.mode}
              markerHover={
                markerHover && JSON.stringify(marker) !== JSON.stringify(oldEndRouteMarker)
              }
              mapRef={mapRef}
              zIndex={zIndex}
              setZIndex={setZIndex}
              {...markersSettings}
            />
          );
        });
      } else if (
        (enabledCyclicMapBehavior && !isFullScreen) ||
        (enabledCyclicMapBehavior && isFullScreen && isMobile)
      ) {
        return renderMarkersFn({
          fareMarkers,
          handleSetMarkerHover,
          isShowTopOffers,
          oldEndRouteMarker,
          markerHover,
          mapProps: props,
          mapRef,
          zIndex,
          setZIndex,
          markersSettings,
          cyclicMapPaddingWidth: 0
        });
      }
      // FIXME: Make identity of markersSettings stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markers, isShowTopOffers, oldEndRouteMarker, markerHover]);

    const renderComplementaryFareMarkers = useMemo(() => {
      if (enabledCyclicMapBehavior && isFullScreen && !isMobile) {
        return renderMarkersFn({
          fareMarkers,
          handleSetMarkerHover,
          isShowTopOffers,
          oldEndRouteMarker,
          markerHover,
          mapProps: props,
          mapRef,
          zIndex,
          setZIndex,
          markersSettings,
          cyclicMapPaddingWidth: cyclicMapPaddingWidth
        });
      }
      // FIXME: Make identity of markersSettings stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markers, isShowTopOffers, oldEndRouteMarker, markerHover, viewPortChange]);

    const getLineData = (
      distance: number,
      thetaOffset: number,
      theta: number,
      point1: number[],
      point2: number[]
    ) => {
      const r2 = distance / 2 / Math.cos(thetaOffset),
        theta2 = theta + thetaOffset;
      const midpointX = r2 * Math.cos(theta2) + point1[1],
        midpointY = r2 * Math.sin(theta2) + point1[0];
      return bezierSpline({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [point1[1], point1[0]],
            [midpointX, midpointY],
            [point2[1], point2[0]]
          ]
        }
      });
    };

    const calculateBezierSpline = (point1: number[], point2: number[]) => {
      const thetaOffset = 3.14 / 10;
      if (enabledCyclicMapBehavior) {
        const leftOrigin = [point1[0], point1[1] - 360];
        const rightOrigin = [point1[0], point1[1] + 360];
        const offsetXLeft = point2[1] - leftOrigin[1],
          offsetYLeft = point2[0] - leftOrigin[0];
        const offsetXRight = point2[1] - rightOrigin[1],
          offsetYRight = point2[0] - rightOrigin[0];
        const offsetX = point2[1] - point1[1],
          offsetY = point2[0] - point1[0];
        const distanceLeftOriginDestination = Math.sqrt(
          Math.pow(offsetXLeft, 2) + Math.pow(offsetYLeft, 2)
        );
        const distanceRightOriginDestination = Math.sqrt(
          Math.pow(offsetXRight, 2) + Math.pow(offsetYRight, 2)
        );
        const distanceCenterOriginDestination = Math.sqrt(
          Math.pow(offsetX, 2) + Math.pow(offsetY, 2)
        );
        const theta = Math.atan2(offsetY, offsetX);
        const thetaLeft = Math.atan2(offsetYLeft, offsetXLeft);
        const thetaRight = Math.atan2(offsetYRight, offsetXRight);
        const min = Math.min(
          distanceCenterOriginDestination,
          distanceLeftOriginDestination,
          distanceRightOriginDestination
        );
        if (min === distanceCenterOriginDestination) {
          return getLineData(distanceCenterOriginDestination, thetaOffset, theta, point1, point2);
        } else if (min === distanceLeftOriginDestination) {
          return getLineData(
            distanceLeftOriginDestination,
            thetaOffset,
            thetaLeft,
            leftOrigin,
            point2
          );
        } else {
          return getLineData(
            distanceRightOriginDestination,
            thetaOffset,
            thetaRight,
            rightOrigin,
            point2
          );
        }
      } else {
        const offsetX = point2[1] - point1[1],
          offsetY = point2[0] - point1[0];
        const r = Math.sqrt(Math.pow(offsetX, 2) + Math.pow(offsetY, 2)),
          theta = Math.atan2(offsetY, offsetX);
        return getLineData(r, thetaOffset, theta, point1, point2);
      }
    };

    const renderRouteLine = () => {
      if (enabledCyclicMapBehavior) {
        return renderRouteLineFn({
          polylineSettings,
          originToDestinationLine,
          oldEndRouteMarker,
          route,
          showEndMarker,
          mapRef,
          cyclicMapPaddingWidth,
          calculateBezierSpline
        });
      } else {
        return originToDestinationLine &&
          oldEndRouteMarker &&
          route.length > 0 &&
          isMarkerContainedInBounds(oldEndRouteMarker.location[0], oldEndRouteMarker.location[1]) &&
          showEndMarker ? (
          <GeoJSON
            key={oldEndRouteMarker?.label}
            color="#777777"
            // eslint-disable-next-line react/style-prop-object
            style="solid"
            weight={3}
            interactive={false}
            {...polylineSettings}
            data={calculateBezierSpline(route[0].location, oldEndRouteMarker.location)}
          />
        ) : null;
      }
    };

    const renderRouteLineHover = () => {
      const basicLineHoverProps = {
        color: '#777777',
        dashArray: '10,10',
        weight: 3,
        interactive: false,
        ...polylineSettingsDestinationHover
      };
      return (
        !isMobile &&
        displayRouteOnHover &&
        markerHover &&
        route.length >= 1 &&
        (enabledCyclicMapBehavior &&
        isValidLocation(route[0]?.location) &&
        isValidLocation(markerHover?.location) ? (
          <>
            <GeoJSON
              {...basicLineHoverProps}
              data={calculateBezierSpline(route[0].location, markerHover.location)}
            />
            <GeoJSON
              {...basicLineHoverProps}
              data={calculateBezierSpline(
                [route[0].location[0], route[0].location[1] - 360],
                [markerHover.location[0], markerHover.location[1] - 360]
              )}
            />
            <GeoJSON
              {...basicLineHoverProps}
              data={calculateBezierSpline(
                [route[0].location[0], route[0].location[1] + 360],
                [markerHover.location[0], markerHover.location[1] + 360]
              )}
            />
          </>
        ) : (
          <GeoJSON
            {...basicLineHoverProps}
            data={calculateBezierSpline(route[0].location, markerHover.location)}
          />
        ))
      );
    };

    const makeMoveRef = useRef(false);
    useEffect(() => {
      if (oldEndRouteMarker !== null && oldEndRouteMarker !== undefined) {
        makeMoveRef.current = true;
      }
    }, [oldEndRouteMarker]);

    useEffect(() => {
      if (oldEndRouteMarker) {
        const cardData = mapRef?.current?.container
          ?.querySelector('.openFareItemCard')
          ?.getBoundingClientRect();
        const boundData = mapRef?.current?.container?.getBoundingClientRect();
        if (cardData && boundData && makeMoveRef.current) {
          if (
            cardData.y - boundData.y <= topMargin ||
            cardData.y + cardData.height - (boundData.y + boundData.height) >= bottomMargin
          ) {
            moveTo(oldEndRouteMarker.location);
            makeMoveRef.current = false;
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moveTo, oldEndRouteMarker]);
    const renderBeginRouteMarker =
      route.length > 0 ? (
        <>
          {enabledCyclicMapBehavior ? (
            renderBeginRouteMarkerFn({
              route,
              beginRouteSettings,
              mapRef,
              cyclicMapPaddingWidth: isFullScreen ? (isMobile ? 0 : cyclicMapPaddingWidth) : 0
            })
          ) : (
            <BeginRouteMarker marker={route[0]} {...beginRouteSettings} />
          )}
        </>
      ) : null;

    const renderEndRouteMarker = () => {
      if (enabledCyclicMapBehavior) {
        return renderEndRouteMarkerFn({
          oldEndRouteMarker,
          showEndMarker,
          isMobile,
          endRouteSettings,
          onClickCloseBtn,
          filterState,
          flightTypeMap,
          mapRef,
          cyclicMapPaddingWidth
        });
      } else {
        return oldEndRouteMarker &&
          isMarkerContainedInBounds(oldEndRouteMarker.location[0], oldEndRouteMarker.location[1]) &&
          showEndMarker ? (
          <EndRouteMarker
            marker={
              isNoFareMarker(oldEndRouteMarker)
                ? {
                    ...oldEndRouteMarker,
                    journeyType: filterState.flightType
                      ? flightTypeMap[filterState.flightType as ReadingTypes]
                      : flightTypeMap['ROUND_TRIP'],
                    departureDate: filterState.dateRange?.start,
                    returnDate: filterState.dateRange?.end
                  }
                : oldEndRouteMarker
            }
            isMobile={isMobile}
            {...endRouteSettings}
            showCloseButton={endRouteSettings.endRouteSettings ?? true}
            onClickCloseBtn={onClickCloseBtn}
          />
        ) : null;
      }
    };

    const translatePositionButtons = (position: string, settings: $TSFixMe): string => {
      const positionBase = {
        topleft: 'topright',
        topright: 'topleft',
        bottomleft: 'bottomright',
        bottomright: 'bottomleft'
      };
      const element: keyof Position = settings.position ?? position;
      if (direction === 'rtl') {
        return positionBase[element];
      }
      return element;
    };

    const controlButtonsEnabled = useMemo(() => {
      if (isMobile) {
        if (!overlay) {
          return true;
        }
      } else {
        if (isOriginSelected) {
          return true;
        }
      }
      return false;
    }, [isMobile, isOriginSelected, overlay]);

    const newHeight = useRef<number | null>(null);

    const tmp = mapRef?.current?.container?.querySelectorAll('.controlAlertInfo')[0];
    useEffect(() => {
      if (isOriginSelected) {
        if (enableCluster) {
          const rect = mapRef?.current?.container
            ?.querySelectorAll('.controlAlertInfo')[0]
            ?.getBoundingClientRect();
          if (rect) {
            newHeight.current = rect.height;
          }
        } else {
          newHeight.current = 0;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOriginSelected, tmp, isFullScreen]);

    const CardInfo = enableCluster && (
      <Control
        {...infoControlBtnContainerSettings}
        position={translatePositionButtons('topleft', infoControlBtnContainerSettings)}
        className="fr-position-relative fr-w-100 fr-ml-0 fr-mt-0 controlAlertInfo"
      >
        {isOriginSelected && showAlertInfo && (
          <AlertInfo
            {...infoControlBtnSettings}
            onCloseAction={() => hideAlertInfo()}
            enableFocusedBox={false}
          />
        )}
      </Control>
    );

    const CardSpace = (
      <Control
        {...infoControlBtnContainerSettings}
        position={translatePositionButtons('topright', infoControlBtnContainerSettings)}
        className="fr-position-relative fr-w-100 fr-ml-0 fr-mt-0 fr-invisible"
      >
        {isOriginSelected && showAlertInfo && (
          <Box
            className={'fr-imc-button-fill-hide'}
            style={newHeight.current ? { height: newHeight.current } : {}}
          />
        )}
      </Control>
    );

    const CardControl = showCardControl && !isFullScreen && (
      <Control
        {...cardControlBtnContainerSettings}
        position={translatePositionButtons('topleft', cardControlBtnContainerSettings)}
      >
        {isOriginSelected && (
          <CardControlCheckbox {...cardControlBtnSettings} disabled={!controlButtonsEnabled} />
        )}
      </Control>
    );

    const CenterControl = showCenterControl && (
      <Control
        {...centerBtnContainerSettings}
        position={translatePositionButtons('bottomright', centerBtnContainerSettings)}
      >
        {isOriginSelected && (
          <Button
            className="control-btn"
            onClick={handleCenterMap}
            tabIndex={controlButtonsEnabled ? 0 : -1}
            tooltip={translate('cmp-interactive-map-container-center-control-btn', 'Center origin')}
            {...centerBtnSettings}
          >
            <Icon icon="center-focus" viewBox="0 0 24 24" size={24} {...centerBtnIconSettings} />
          </Button>
        )}
      </Control>
    );

    const toggleFullScreenButtonRef = useRef<any>(null);

    useEffect(() => {
      if (isFullScreen !== undefined && !isFullScreen) {
        setTimeout(() => toggleFullScreenButtonRef?.current?.focus());
      }
    }, [isFullScreen]);

    const FullScreenControl = isOriginSelected &&
      showFullScreenControl &&
      newHeight.current !== null && (
        <Control
          {...fullScreenBtnContainerSettings}
          position={translatePositionButtons('topright', fullScreenBtnContainerSettings)}
        >
          <Button
            className="control-btn"
            tooltip={translate(
              'cmp-interactive-map-container-full-screen-control-btn',
              'Toggle FullScreen'
            )}
            tabIndex={controlButtonsEnabled ? 0 : -1}
            onClick={handleToggleFullScreen}
            {...fullScreenBtnSettings}
            focusReferences={(e: any) => {
              toggleFullScreenButtonRef.current = e;
            }}
          >
            <Icon
              icon={`${isFullScreen ? 'full-screen-exit' : 'crop-free'}`}
              viewBox="0 0 24 24"
              size={24}
              {...fullScreenBtnIconSettings}
            />
          </Button>
        </Control>
      );

    const ZoomControls = showZoomControl && (
      <Control
        {...zoomControlsSettings}
        position={translatePositionButtons('bottomright', zoomControlsSettings)}
      >
        {isOriginSelected && (
          <Box {...zoomControlsContainerSettings}>
            {showZoomValue && (
              <Button
                className="control-btn"
                tooltip={translate('cmp-interactive-map-container-zoom-in-control-btn', 'Zoom In')}
                {...zoomInBtnSettings}
              >
                <Text>{actualZoom}</Text>
              </Button>
            )}
            <Button
              onClick={handleZoomIn}
              disabled={actualZoom >= maxZoom}
              className="control-btn"
              tabIndex={controlButtonsEnabled ? 0 : -1}
              tooltip={translate('cmp-interactive-map-container-zoom-in-control-btn', 'Zoom In')}
              {...zoomInBtnSettings}
            >
              <Icon icon="add" viewBox="0 0 24 24" size={24} {...zoomInBtnIconSettings} />
            </Button>
            <Button
              onClick={handleZoomOut}
              disabled={actualZoom <= minZoom}
              className="control-btn"
              tabIndex={controlButtonsEnabled ? 0 : -1}
              tooltip={translate('cmp-interactive-map-container-zoom-out-control-btn', 'Zoom Out')}
              {...zoomOutBtnSettings}
            >
              <Icon icon="minus" viewBox="0 0 24 24" size={24} {...zoomOutBtnIconSettings} />
            </Button>
          </Box>
        )}
      </Control>
    );

    // Set up map boundaries
    const bounds = useMemo(
      () =>
        leaflet.latLngBounds(
          leaflet.latLng(maxBounds.northEast[0], maxBounds.northEast[1]),
          leaflet.latLng(maxBounds.southWest[0], maxBounds.southWest[1])
        ),
      [maxBounds, leaflet]
    );

    //INFO: Avoiding continuous questions within a cycle of conditionals
    const hidePointWithUserFilter = useMemo(() => {
      return (
        Boolean(filterState.budget) ||
        (filterState.flightStopCount ? Boolean(filterState.flightStopCount !== 'Any') : false)
      );
    }, [filterState.budget, filterState.flightStopCount]);

    const points = useMemo(
      () =>
        markers
          .filter((item) => {
            //INFO: ignoring this point because I was double rendering the point which causes a double tooltip
            if (
              (!isMobile &&
                oldEndRouteMarker &&
                JSON.stringify(oldEndRouteMarker.location) === JSON.stringify(item.location)) ||
              // in some cases the backend returns the origin itself as destination airports. It happens only for Spirit selecting MCO as source
              (route &&
                route[0] &&
                JSON.stringify(route[0].location) === JSON.stringify(item.location))
            ) {
              return null;
            }
            return item;
          })
          //INFO: Removing markers of type `fares` from the cluster as they were represented within the cluster
          .filter((item) => {
            if (fareMarkers.find((fareMarker) => fareMarker.fare.destination === item.value)) {
              return null;
            } else {
              return item;
            }
          })
          .filter((item) => {
            //INFO: https://trello.com/c/xbM4mNy0
            //INFO: Ignoring this point because I was double rendering the point which causes a double tooltip
            if (hidePointWithUserFilter) {
              if (fareMarkers.find((fareMarker) => fareMarker.fare.destination === item.value)) {
                return item;
              } else {
                return null;
              }
            }
            return item;
          })
          .map((marker, index) => ({
            type: 'Feature',
            properties: { cluster: false, id: index, ...marker },
            geometry: {
              type: 'Point',
              coordinates: [marker.location[1], marker.location[0]]
            }
          })),
      [fareMarkers, hidePointWithUserFilter, markers, oldEndRouteMarker, route, isMobile]
    );

    const onlyDestinationPoints = useMemo(
      () =>
        destinationMapReadyOnly.map((marker, index) => ({
          type: 'Feature',
          properties: { cluster: false, id: index, ...marker },
          geometry: {
            type: 'Point',
            coordinates: [marker.location[1], marker.location[0]]
          }
        })),
      [destinationMapReadyOnly]
    );

    const getCurrentBounds = useCallback((): [number, number, number, number] | undefined => {
      if (mapRef.current) {
        const bounds = mapRef.current.leafletElement.getBounds();
        return [
          bounds.getSouthWest().lng,
          bounds.getSouthWest().lat,
          bounds.getNorthEast().lng,
          bounds.getNorthEast().lat
        ];
      }
      return undefined;
    }, []);

    const [clusterOptions, setClusterOptions] = useState(() => ({
      points,
      bounds: getCurrentBounds(),
      zoom: actualZoom
    }));

    const [clusterDestinationOptions, setClusterDestinationOptions] = useState(() => ({
      points: onlyDestinationPoints,
      bounds: getCurrentBounds(),
      zoom: actualZoom
    }));

    useEffect(() => {
      const updateClusterOptions = debounce(() => {
        setClusterOptions({
          points,
          bounds: getCurrentBounds(),
          zoom: actualZoom
        });
        setClusterDestinationOptions({
          points: onlyDestinationPoints,
          bounds: getCurrentBounds(),
          zoom: actualZoom
        });
      }, 30);
      updateClusterOptions();
    }, [points, actualZoom, getCurrentBounds, onlyDestinationPoints]);

    const { clusters, supercluster } = useSupercluster({
      ...clusterOptions,
      options: { radius: 75, maxZoom, ...clusterSettings } //INFO: https://github.com/mapbox/supercluster#options
    });

    const { clusters: clusterDestination } = useSupercluster({
      ...clusterDestinationOptions,
      options: { radius: 75, maxZoom, ...clusterSettings } //INFO: https://github.com/mapbox/supercluster#options
    });

    const renderDestinationCluster = useMemo(() => {
      return clusterDestination.map((cluster) => {
        // every cluster point has coordinates
        const [longitude, latitude] = cluster.geometry.coordinates;
        // the point may be either a cluster or a crime point
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;

        // we have a cluster to render
        if (isCluster && enableCluster) {
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              position={[latitude, longitude]}
              icon={createClusterCustomIcon(pointCount)}
            />
          );
        }
        //INFO: Avoiding double rendering on fares that have data
        if (cluster.properties.markerType !== 'fare') {
          return (
            <VectorMapMarker
              onMouseOver={handleSetMarkerHover}
              showTopOffers={isShowTopOffers}
              key={`marker-${cluster.properties.id}`}
              marker={cluster.properties}
              isMapMode={!!props?.mapMode?.mode}
              markerHover={markerHover}
              mapRef={mapRef}
              zIndex={zIndex}
              setZIndex={setZIndex}
              {...markersSettings}
            />
          );
        }
        return null;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusterDestination]);

    const renderCluster = useMemo(() => {
      if (!enabledCyclicMapBehavior) {
        return clusters.map((cluster) => {
          // every cluster point has coordinates
          const [longitude, latitude] = cluster.geometry.coordinates;
          // the point may be either a cluster or a crime point
          const { cluster: isCluster, point_count: pointCount } = cluster.properties;

          // we have a cluster to render
          if (isCluster && enableCluster) {
            return (
              <Marker
                key={`cluster-${cluster.id}`}
                position={[latitude, longitude]}
                icon={createClusterCustomIcon(pointCount)}
                onClick={() => {
                  const expansionZoom = Math.min(
                    supercluster.getClusterExpansionZoom(cluster.id),
                    maxZoom
                  );
                  const leaflet = mapRef.current?.leafletElement;
                  leaflet?.setView([latitude, longitude], expansionZoom, {
                    animate: true
                  });
                  if (enableHoverShapeCluster) {
                    setPolygon([]);
                  }
                }}
                onMouseOver={(e: any) =>
                  enableHoverShapeCluster &&
                  setPolygon(
                    supercluster
                      .getChildren(cluster.id)
                      .map((item: { geometry: { coordinates: any[] } }) => [
                        item.geometry.coordinates[1],
                        item.geometry.coordinates[0]
                      ])
                  )
                }
                onMouseOut={() => enableHoverShapeCluster && setPolygon([])}
              />
            );
          }
          //INFO: Avoiding double rendering on fares that have data
          if (cluster.properties.markerType !== 'fare') {
            //INFO: https://trello.com/c/xbM4mNy0
            if (hidePointWithUserFilter) {
              if (
                fareMarkers.find(
                  (fareMarker) => fareMarker.fare.destination === cluster.properties.value
                )
              ) {
                return (
                  <VectorMapMarker
                    onMouseOver={handleSetMarkerHover}
                    showTopOffers={isShowTopOffers}
                    key={`marker-${cluster.properties.id}`}
                    marker={cluster.properties}
                    isMapMode={!!props?.mapMode?.mode}
                    markerHover={markerHover}
                    mapRef={mapRef}
                    zIndex={zIndex}
                    setZIndex={setZIndex}
                    {...markersSettings}
                  />
                );
              }
            } else {
              return (
                <VectorMapMarker
                  onMouseOver={handleSetMarkerHover}
                  showTopOffers={isShowTopOffers}
                  key={`marker-${cluster.properties.id}`}
                  marker={cluster.properties}
                  isMapMode={!!props?.mapMode?.mode}
                  markerHover={markerHover}
                  mapRef={mapRef}
                  zIndex={zIndex}
                  setZIndex={setZIndex}
                  {...markersSettings}
                />
              );
            }
          }
          return null;
        });
      } else if (
        (enabledCyclicMapBehavior && !isFullScreen) ||
        (enabledCyclicMapBehavior && isFullScreen && isMobile)
      ) {
        return renderClusterFn({
          clusters,
          clusterSizesKeys,
          clusterSizes,
          supercluster,
          maxZoom,
          mapRef,
          enableHoverShapeCluster,
          setPolygon,
          enableCluster,
          handleSetMarkerHover,
          isShowTopOffers,
          mapProps: props,
          markerHover,
          zIndex,
          setZIndex,
          hidePointWithUserFilter,
          fareMarkers,
          markersSettings,
          classNameCluster,
          cyclicMapPaddingWidth: 0
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusters, markerHover]);

    const renderComplementaryCluster = useMemo(() => {
      if (enabledCyclicMapBehavior && isFullScreen && !isMobile) {
        return renderClusterFn({
          clusters,
          clusterSizesKeys,
          clusterSizes,
          supercluster,
          maxZoom,
          mapRef,
          enableHoverShapeCluster,
          setPolygon,
          enableCluster,
          handleSetMarkerHover,
          isShowTopOffers,
          mapProps: props,
          markerHover,
          zIndex,
          setZIndex,
          hidePointWithUserFilter,
          fareMarkers,
          markersSettings,
          classNameCluster,
          cyclicMapPaddingWidth
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusters, markerHover, viewPortChange]);

    useEffect(() => {
      if (mapRef?.current) {
        let tmp = mapRef?.current.container?.querySelectorAll('.markerClusterLargeLabel');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('aria-hidden', 'true');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.origin-route-popup');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('aria-hidden', 'true');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterMediumLabel');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('aria-hidden', 'true');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterLargeXLLabel');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('aria-hidden', 'true');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterSmallLabel');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('aria-hidden', 'true');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterSmall');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('tabindex', '-1');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterLargeX');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('tabindex', '-1');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterLargeXL');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('tabindex', '-1');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterMedium');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('tabindex', '-1');
          }
        }

        tmp = mapRef?.current.container?.querySelectorAll('.markerClusterLarge');
        if (tmp && tmp?.length > 0) {
          for (let index = 0; index < tmp.length; index++) {
            const element = tmp[index];
            element?.setAttribute('tabindex', '-1');
          }
        }
      }
    }, [renderCluster]);

    const renderHoverShapeCluster = useMemo(() => {
      return (
        enableHoverShapeCluster && (
          <Polygon positions={polygon} {...clusterSettings.polygonOptions} />
        )
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polygon]);

    const MarkerNames = useMemo(() => {
      return (
        markerNames &&
        Array.isArray(markerNames) &&
        markerNames.map((item: $TSFixMe) => {
          return <NameMarker marker={item} />;
        })
      );
    }, [markerNames]);

    const defaultFareItemActiveProps = {
      showDate: false,
      showBackgroundImage: false,
      showMiles: false,
      showPill: false,
      showDurationTrip: false,
      fareItemPrice: {
        showPricePromo: false
      },
      showDepartureReturnBeforePrice: true,
      showPrice: true,
      bgColor: 'dark',
      textColor: 'white',
      showHorizontalSeparator: true,
      showCallToAction: true,
      tabIndexCallToAction: false
    };

    const baseCloseBtnStyle = {
      'cmp-styles': {
        position: 'absolute',
        top: '0px',
        'z-Index': '1',
        right: '0px',
        'background-color': 'transparent !important',
        border: 'unset',
        margin: '1px'
      }
    };
    const getNewLabel = (marker: any, labelStructure: string) => {
      return strFormatV2(labelStructure, marker);
    };

    const labelStructure =
      endRouteSettings.checkAvailabilityCardSettings.labelStructure === undefined
        ? '{city.name} ({iataCode})'
        : endRouteSettings.checkAvailabilityCardSettings.labelStructure;

    const renderEndRouteMarkerCenter = (
      <>
        {isMobile &&
        !isLoading &&
        isOriginSelected &&
        oldEndRouteMarker !== undefined &&
        oldEndRouteMarker !== null &&
        isMarkerContainedInBounds(oldEndRouteMarker.location[0], oldEndRouteMarker.location[1]) &&
        showEndMarker ? (
          <Box className="fr-item-card-center" {...newCenterCardOverlayProps}>
            <Box position="relative">
              {(oldEndRouteMarker as FareMarkerData).markerType === 'fare' ? (
                <>
                  <Button
                    ariaLabel={translate('cmp-interactive-map-fare-card-close', 'Close')}
                    onClick={() => {
                      onClickCloseBtn();
                    }}
                    tabIndex={'-1'}
                    ariaHidden={true}
                    {...baseCloseBtnStyle}
                    {...endRouteSettings.fareItemCardSettings.closeButtonSettings}
                  >
                    <Icon
                      icon={'close'}
                      {...endRouteSettings.fareItemCardSettings.closeBtnIconSettings}
                    />
                  </Button>
                  <FareItemCard
                    data={(oldEndRouteMarker as FareMarkerData).fare}
                    {...defaultFareItemActiveProps}
                    {...endRouteSettings.fareItemCardSettings}
                    ariaHidden={true}
                  ></FareItemCard>
                </>
              ) : (
                <CheckAvailabilityCard
                  onClickCloseBtn={() => {
                    onClickCloseBtn();
                  }}
                  {...endRouteSettings.checkAvailabilityCardSettings}
                  fareItemSettings={{
                    data: {
                      dataType: 'EMPTY_FROM_API',
                      origin: (oldEndRouteMarker as any)?.origin.value,
                      destination: oldEndRouteMarker?.value,
                      journeyType: filterState.flightType
                        ? flightTypeMap[filterState.flightType as ReadingTypes]
                        : flightTypeMap['ROUND_TRIP'],
                      departureDate:
                        filterState.dateRange?.start !== undefined
                          ? dayjs(filterState.dateRange?.start).format('YYYY-MM-DD')
                          : '',
                      returnDate:
                        filterState.dateRange?.end !== undefined
                          ? dayjs(filterState.dateRange?.end).format('YYYY-MM-DD')
                          : ''
                    },
                    fareItemNoPriceAvailable: {
                      showLinkAvailability: true,
                      ...(endRouteSettings.checkAvailabilityCardSettings.fareItemSettings
                        ? endRouteSettings.checkAvailabilityCardSettings.fareItemSettings
                            .fareItemNoPriceAvailable
                        : {})
                    },
                    ...defaultFareItemActiveProps,
                    ...endRouteSettings.checkAvailabilityCardSettings.fareItemSettings
                  }}
                  originDestinationSettings={{
                    ...{
                      destinationCity: getNewLabel(oldEndRouteMarker, labelStructure),
                      originCity: (oldEndRouteMarker as any).origin.label,
                      origin: oldEndRouteMarker?.value,
                      destination: oldEndRouteMarker?.value,
                      showDestinationAirportCode: false
                    },
                    ...endRouteSettings.checkAvailabilityCardSettings.originDestinationSettings
                  }}
                />
              )}
            </Box>
          </Box>
        ) : null}
      </>
    );

    return (
      <>
        {overlay && isMobile && !isLoading && isOriginSelected && !isFullScreen && (
          <Box onClick={handleToggleFullScreen} {...newOverlayProps}>
            {overlayLabelKey && (
              <Text {...newOverlayLabelProps}>
                {translate(overlayLabelKey, 'Click to activate interactivity on the map')}
              </Text>
            )}
          </Box>
        )}
        <Map
          worldCopyJump={enabledCyclicMapBehavior}
          ref={mapRef}
          center={initCenter}
          zoom={actualZoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          onmouseover={() => {
            setMarkerHover(undefined);
          }}
          // className="map"
          className={`map ${classNameColor}`}
          zoomControl={false}
          onClick={handleClick}
          attributionControl={false}
          maxBounds={enabledCyclicMapBehavior && isFullScreen ? undefined : bounds}
          keyboard={false}
          onZoomEnd={handleZoomEnd}
          onDragEnd={handleDragEnd}
          ondragstart={handleDragStart}
          onMoveEnd={() => removeTabIndex()}
          boxZoom={
            enableCenterCard
              ? !isMobile ||
                (isMobile &&
                  isFullScreen &&
                  (oldEndRouteMarker === undefined || oldEndRouteMarker === null))
              : !isMobile || (isMobile && isFullScreen)
          }
          dragging={
            enableCenterCard
              ? !isMobile ||
                (isMobile &&
                  isFullScreen &&
                  (oldEndRouteMarker === undefined || oldEndRouteMarker === null))
              : !isMobile || (isMobile && isFullScreen)
          }
          touchZoom={
            enableCenterCard && oldEndRouteMarker !== undefined && oldEndRouteMarker !== null
              ? false
              : true
          }
          doubleClickZoom={
            enableCenterCard && oldEndRouteMarker !== undefined && oldEndRouteMarker !== null
              ? false
              : true
          }
          preferCanvas={true} // Rendering the map to a canvas instead of an svg
          maxBoundsViscosity={1} // Preventing the user from dragging outside the bounds.
          {...mapSettings}
        >
          {GeoJsonMap()}
          {renderHoverShapeCluster}
          {isMobile ? (enableCenterCard ? null : renderRouteLine()) : renderRouteLine()}
          {renderRouteLineHover()}
          {renderDestinationCluster}
          {enabledCyclicMapBehavior && isFullScreen && !isMobile
            ? renderComplementaryCluster
            : renderCluster}
          {enabledCyclicMapBehavior && isFullScreen && !isMobile
            ? renderComplementaryFareMarkers
            : renderFareMarkers}
          {renderMarkers}
          {renderBeginRouteMarker}
          {isMobile
            ? enableCenterCard
              ? renderEndRouteMarkerCenter
              : renderEndRouteMarker()
            : renderEndRouteMarker()}
          {CardSpace}
          {CardInfo}
          {CardControl}
          {ZoomControls}
          {FullScreenControl}
          {CenterControl}
          {MarkerNames}
        </Map>
      </>
    );
  }
);

export default VectorMap;
